import { useCallback, useState } from 'react';

import { TaskGroup, TaskGroupParticipantChanges } from '@this+that/models';
import log from 'loglevel';

import { useUpdateTaskGroupParticipants as useUpdateTaskGroupParticipantsService } from '../../services/taskGroups.service';
import { useInboxEntriesStore } from '../../store';

export function useUpdateTaskGroupParticipants() {
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateError, setUpdateError] = useState<Error | null>(null);

  const { updateTaskGroupParticipants: updateTaskGroupParticipantsService } =
    useUpdateTaskGroupParticipantsService();
  const { updateTaskGroup } = useInboxEntriesStore(
    (state) => state.taskManager.taskGroups,
  );

  const handleUpdateTaskGroupParticipants = useCallback(
    async (
      taskGroupId: string,
      participantChanges: TaskGroupParticipantChanges,
    ): Promise<TaskGroup | undefined> => {
      setIsUpdating(true);
      setUpdateError(null);
      try {
        const updatedTaskGroup = await updateTaskGroupParticipantsService(
          taskGroupId,
          participantChanges,
        );
        if (updatedTaskGroup) {
          updateTaskGroup(updatedTaskGroup);
        }
        return updatedTaskGroup ?? undefined;
      } catch (error) {
        const errorInstance =
          error instanceof Error
            ? error
            : new Error('Failed to update task group participants');
        setUpdateError(errorInstance);
        log.error('Error updating task group participants:', error);
        throw errorInstance;
      } finally {
        setIsUpdating(false);
      }
    },
    [updateTaskGroupParticipantsService, updateTaskGroup],
  );

  return { handleUpdateTaskGroupParticipants, isUpdating, updateError };
}
